import { IIntakeIdWithDates, FilterKey } from 'types';
import { all, call, cancelled, fork, put, select, takeLatest, getContext } from 'redux-saga/effects';
import { MATCH_FILTER_NAMES } from 'shared-components';
import * as api from '../../../api/api';
import { IConfigState } from '../../../types';
import { INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '../../../utilities/constants';
import { getActiveStageTab, getDaysAgoInEpochMilliseconds } from '../../../utilities/helpers';
import isTesting from '../../../utilities/isTesting';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { logout } from '../../auth/actions';
import { AuthState, UserRole } from '../../auth/types';
import { getAuth, getConfig, getIntakeId, getIntakeRange, getUI } from '../../selectors';
import {
  setSelectedRows,
  getStudentsFailure,
  getStudentsSuccess,
  getAllStudentsComplete,
  getTotalsSuccess,
  getTotalsFailure,
} from '../actions';
import { DashboardActionTypes } from '../types';
import { shouldSetUniFromState } from './helpers';
import { InstitutionEnum } from '@/components/molecules/ActionsToolbar/types';

export let getAllStudentsAbortController: AbortController;

export function* getStudentSearchParams() {
  const auth: AuthState = yield select(getAuth);
  const config: IConfigState = yield select(getConfig);
  const intakeId: string = yield select(getIntakeId);
  if (!intakeId && !isTesting) {
    return;
  }
  const { headerTab } = yield select(getUI);
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);

  return createGetAllStudentsParams({
    auth,
    config,
    intakeId,
    headerTab,
    intakeRange,
    idToken,
  }) as api.IGetAllParams;
}

export const createGetAllStudentsParams = ({
  auth,
  config,
  intakeId,
  headerTab,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  config: IConfigState;
  intakeId: string;
  headerTab: any;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}) => {
  const params = new URLSearchParams(window.location.search);

  if (!intakeId && !isTesting) {
    return;
  }

  const uni = shouldSetUniFromState(auth.role) ? auth.uni : undefined;
  const isPathway = config.isPathway ?? false;

  const startDate = intakeRange?.startDate ?? INTAKE_ALL_START_DATE;
  const endDate = intakeRange?.endDate ?? INTAKE_ALL_END_DATE;

  const canViewIncludeStudentDropdown = !!(
    config.relatedPathways?.length &&
    hasPerm(auth.role, P.VIEW_INSTITUTION_DROPDOWN) &&
    !(
      [UserRole.UniAdmin, UserRole.Uni, UserRole.UniSecure, UserRole.UniLite].includes(auth.role) &&
      auth.pathwayAccess === 'None'
    )
  );
  getAllStudentsAbortController = new AbortController();
  return {
    abortController: getAllStudentsAbortController,
    idToken,
    stage: decodeURI(getActiveStageTab(params, headerTab)),
    ...(isPathway ? { pathway: uni } : { uni }),
    intakeId,
    pathwayVisibility: canViewIncludeStudentDropdown
      ? (params.get('institution') ??
        localStorage.getItem('dashboard-uni-institution-value') ??
        InstitutionEnum.INSTITUTION_UNI)
      : '',
    startDate,
    endDate,
    page: Number.parseInt(params.get('page') || '1', 10),
    sortCol: params.get('sort-col'),
    sortDir: params.get('sort-dir'),
    search: params.get('search'),
    unreadMessages: params.get('unread_messages'),
    unis: params.getAll(FilterKey.UNIVERSITY),
    studentNo: params.getAll('filter_student_number'),
    surname: params.getAll('filter_surname'),
    givenName: params.getAll('filter_given_name'),
    course: params.getAll(FilterKey.COURSE),
    faculty: params.getAll(FilterKey.FACULTY),
    nationality: params.getAll(FilterKey.NATIONALITY),
    country: params.getAll(FilterKey.COUNTRY_OF_RESIDENCE),
    applicationCountry: params.getAll(FilterKey.APPLICATION_COUNTRY),
    activityStatus: params.getAll(FilterKey.ACTIVITY_STATUS),
    idle: params.getAll('filter_IDLE'),
    complete: params.getAll(FilterKey.COMPLETE),
    incomplete: params.getAll(FilterKey.INCOMPLETE),
    fundsMaturity: params.getAll(FilterKey.FUNDS_MATURITY),
    //region: params.getAll('filter_REGION'),
    market: params.getAll(FilterKey.MARKET),
    visaStatus: params.getAll(FilterKey.VISA_STATUS),
    confidence: params.getAll(FilterKey.CONFIDENCE).filter(item => item !== 'any'),
    agent: params.getAll(FilterKey.AGENT_NAME),
    conditionalStatus: params.getAll(FilterKey.CONDITIONAL_STATUS),
    courseConditionalStatus: params.getAll(FilterKey.COURSE_CONDITIONAL_STATUS),
    evidenceRequirements: params.getAll(FilterKey.EVIDENCE_REQUIREMENTS),
    customRegion: params.getAll(FilterKey.RISK_REGION),
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    services: params.getAll(FilterKey.SERVICES),
    immigrationProviders: params.getAll(FilterKey.IMMIGRATION_PROVIDERS),
    interview: params.getAll(FilterKey.INTERVIEW),
    recordedInterview: params.getAll(FilterKey.RECORDED_INTERVIEW),
    idleFrom: params.has(FilterKey.MAXIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MAXIMUM_DAYS_IDLE).map(Number)[0]),
    ],
    idleTo: params.has(FilterKey.MINIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MINIMUM_DAYS_IDLE).map(Number)[0]) - 1,
    ],
    dependants: params.getAll(FilterKey.DEPENDANTS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),
    shareCode: params.getAll(FilterKey.SHARE_CODE),
  } as api.IGetAllParams;
};

export function* handleGetAllStudents(): any {
  const params = new URLSearchParams(window.location.search);
  let res: any;
  const auth: AuthState = yield select(getAuth);
  const config = yield select(getConfig);
  const intakeIdAuth: string = yield select(getIntakeId);
  const intakeId = params.get('intakeId') || intakeIdAuth;
  if (!intakeId && !isTesting) {
    return;
  }

  const { headerTab } = yield select(getUI);
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      return;
    }
    const getAllStudentsParams = createGetAllStudentsParams({
      auth,
      config,
      intakeId,
      headerTab,
      intakeRange,
      idToken,
    });
    if (!getAllStudentsParams) {
      return;
    }

    getAllStudentsAbortController = new AbortController();
    res = yield call(api.getAllStudents, getAllStudentsParams);
    yield put(setSelectedRows([]));
  } catch (err) {
    yield put(getStudentsFailure(err));
    return;
  } finally {
    if (yield cancelled()) {
      // the task was cancelled
      getAllStudentsAbortController.abort();
      return;
    }
  }
  yield put(getStudentsSuccess(res.students, auth, config));

  yield put(getAllStudentsComplete());
}

export let getTotalsAbortController: AbortController;

export function* handleGetTotals(): any {
  const auth: AuthState = yield select(getAuth);
  const config = yield select(getConfig);
  const intakeId: string = yield select(getIntakeId);

  if (!intakeId && !isTesting) {
    return;
  }

  const { headerTab } = yield select(getUI);
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      return;
    }

    const getAllStudentsParams = createGetAllStudentsParams({
      auth,
      config,
      intakeId,
      headerTab,
      intakeRange,
      idToken,
    });

    if (!getAllStudentsParams) {
      return;
    }

    // Create a new abort controller for totals request
    getTotalsAbortController = new AbortController();

    // Remove pagination params for totals request
    const totalsParams = {
      ...getAllStudentsParams,
      page: undefined,
      sortCol: undefined,
      sortDir: undefined,
      abortController: getTotalsAbortController,
    };

    const res = yield call(api.getStudentTotals, totalsParams);

    yield put(getTotalsSuccess(res.totals));
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return;
    }
    yield put(getTotalsFailure(err));
  } finally {
    if (yield cancelled()) {
      // the task was cancelled
      if (getTotalsAbortController) {
        getTotalsAbortController.abort();
      }
    }
  }
}

function* watchGetStudents() {
  yield takeLatest(DashboardActionTypes.GET_STUDENTS, handleGetAllStudents);
}

function* watchGetTotals() {
  yield takeLatest(DashboardActionTypes.GET_TOTALS, handleGetTotals);
}

function* GetStudentsSaga() {
  yield all([fork(watchGetStudents), fork(watchGetTotals)]);
}

export default GetStudentsSaga;
